import cuid from "cuid";
import { defaults, find, get, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { INITIAL_VALUES, SCREENS, USER_TYPES } from "utils/constants";
import View from "./View";
import { useContext } from "react";
import { StoreContext } from "shared/store";

const Detail = () => {
  const { user } = useContext(StoreContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isCurrentBroker = id === get(user, "organization.broker.id");
  const onDelete = () =>
    navigate(`${SCREENS.PRIVATE.ADMIN.BROKERS.DELETE.path}/${id}`);
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.BROKERS.LIST.path);
  const [updateUser] = useMutation(graphql.mutations.UPDATE_USER_AUTH);
  const [updateUserParams] = useMutation(graphql.mutations.UPDATE_USER, {
    refetchQueries: [
      {
        query: graphql.queries.USERS,
        awaitRefetchQueries: true,
      },
    ],
  });
  const [acceptSignUp] = useMutation(graphql.mutations.ACCEPT_SIGN_UP);

  const { data: distributions, loading: distributionsLoading } = useQuery(
    graphql.queries.DISTRIBUTIONS,
    { variables: { where: { broker: { id } } } },
  );
  const [updateBroker] = useMutation(graphql.mutations.UPDATE_BROKER, {
    refetchQueries: [
      {
        query: graphql.queries.BROKERS,
        awaitRefetchQueries: true,
        variables: {
          ...INITIAL_VALUES,
        },
      },
      {
        query: graphql.queries.BROKER,
        awaitRefetchQueries: true,
        variables: { where: { id } },
      },
      {
        query: graphql.queries.DISTRIBUTIONS,
        awaitRefetchQueries: true,
        variables: { where: { broker: { id } } },
      },
    ],
    onCompleted: () => {
      navigate(SCREENS.PRIVATE.ADMIN.BROKERS.LIST.path);
    },
  });

  const { data, loading, error } = useQuery(graphql.queries.BROKER, {
    variables: { where: { id } },
    fetchPolicy: "network-only",
  });

  const updateParams = ({ userId, params }) => {
    updateUserParams({
      variables: {
        where: { id: userId },
        data: { params },
      },
    });
  };

  const onSubmit = ({
    telecoms,
    iconAttachment,
    organization,
    user,
    icon,
    distributions,
    globalProjectsViewEnabled,
    hotListEnabled,
    hotListSourceName,
    hotListSignalFeed,
    leadTypologyEnabled,
    ...values
  }) => {
    const organizationTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    const existingConfig = omitDeep(organization.config || {}, "__typename");
    return updateBroker({
      variables: {
        where: {
          id,
        },
        data: {
          user: {
            ...user,
            organization: {
              ...organization,
              config: {
                ...existingConfig,
                globalProjectsViewEnabled: globalProjectsViewEnabled === true,
                hotList: {
                  enabled: hotListEnabled === true,
                  sourceName: (hotListSourceName || "").trim() || null,
                  signalFeed: hotListSignalFeed === true,
                },
                leadTypologyEnabled: leadTypologyEnabled === true,
              },
              telecoms: organizationTelecoms,
              icon: iconAttachment,
              users: undefined,
              contracts: undefined,
            },
          },
          ...(!isCurrentBroker && {
            distributions: distributions?.map(({ commissions, ...rest }) => ({
              ...rest,
              commissions: commissions?.map((commission) => ({
                id: commission,
              })),
            })),
          }),
          ...values,
        },
      },
    });
  };

  if (loading || distributionsLoading || error) return <Loading />;

  const broker = omitDeep(data.broker, "__typename");
  const adminUser = find(
    get(broker, "organization.users", []),
    (organizationUser) =>
      get(organizationUser, "meta.type") === USER_TYPES.BROKER_ADMIN.type,
  );

  return (
    <View
      onSubmit={onSubmit}
      data={{
        ...broker,
        user: adminUser || {},
      }}
      distributions={omitDeep(
        get(distributions, "distributions.data", []),
        "__typename",
      )}
      cancel={cancel}
      onDelete={onDelete}
      acceptSignUp={acceptSignUp}
      updateParams={updateParams}
      isCurrentBroker={isCurrentBroker}
    />
  );
};

export default Detail;
